import { useUser } from "@clerk/clerk-react";
import { Moon, Sun, User, HelpCircle, Code, LogOut, ChevronUp, Sparkles, CreditCard } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui-components/ui/dropdown-menu";
import { useOptimizedAuthContext } from "@/contexts/OptimizedAuthContext";
import { useEntitlement } from "@/contexts/EntitlementContext";
import { useFlag } from "@/contexts/FlagsContext";
import { useRouter } from "@/hooks/useRouter";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils/classnames";

/** Bottom-of-ribbon identity: avatar + name + plan, with a menu for profile, the
 * dark-mode toggle, docs, and log out. Collapsed → avatar only. */
export function UserPill({ collapsed = false }: { collapsed?: boolean }) {
  const { user } = useUser();
  const { signOut } = useOptimizedAuthContext();
  const { plan, loading: planLoading } = useEntitlement();
  const showBilling = useFlag("billing");
  const { theme, toggleTheme } = useTheme();
  const router = useRouter();

  const name = user?.fullName ?? user?.emailAddresses?.[0]?.emailAddress ?? "Account";
  const email = user?.emailAddresses?.[0]?.emailAddress ?? "";
  const initial = name[0]?.toUpperCase() || "U";
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
  // Entitlement defaults to free while loading — gate the upsell on !loading
  // so paid users don't see it flash during the fetch.
  const isFree = !planLoading && plan === "free";
  const isPaid = !planLoading && plan !== "free";

  // Straight to Stripe checkout — the user clicking "Upgrade" has already made
  // the decision the feature-gate UpgradeDialog exists to prompt. `next`
  // returns them here after checkout; `src` rides into the /checkout pageview
  // for funnel attribution.
  const goUpgrade = () =>
    router.push(
      `/checkout?plan=pro&next=${encodeURIComponent(window.location.pathname)}&src=user-pill`,
    );

  const avatar = (
    <span className="flex-shrink-0 h-7 w-7 rounded-full bg-brand-strong flex items-center justify-center overflow-hidden">
      {user?.imageUrl ? (
        <img src={user.imageUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-brand-foreground text-xs font-semibold">{initial}</span>
      )}
    </span>
  );

  return (
    <>
      <div className={cn("border-t border-border", collapsed ? "flex justify-center py-2" : "p-2")}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex items-center rounded-md transition-colors hover:bg-muted",
                collapsed ? "h-9 w-9 justify-center" : "w-full px-1.5 py-1.5 gap-2",
              )}
              title={collapsed ? name : undefined}
            >
              {avatar}
              {!collapsed && (
                <>
                  <span className="flex-1 min-w-0 text-left">
                    <span className="block truncate text-sm font-medium text-foreground">{name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {planLabel} plan
                      {isFree && (
                        <>
                          {" · "}
                          <span className="font-medium text-brand">Upgrade</span>
                        </>
                      )}
                    </span>
                  </span>
                  <ChevronUp size={14} className="flex-shrink-0 text-muted-foreground" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium text-foreground truncate">{name}</p>
              {email && <p className="text-xs text-muted-foreground truncate">{email}</p>}
            </div>
            <DropdownMenuSeparator />
            {isFree && (
              <>
                <DropdownMenuItem
                  className="cursor-pointer text-brand focus:text-brand"
                  onClick={goUpgrade}
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Upgrade to Pro
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {isPaid && showBilling && (
              <>
                <DropdownMenuItem className="cursor-pointer" onClick={() => router.push("/billing")}>
                  <CreditCard className="w-4 h-4 mr-2" /> Manage billing
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {/* Settings' Account tab embeds the same Clerk <UserProfile> this
                used to open in a portal modal — one canonical surface. */}
            <DropdownMenuItem className="cursor-pointer" onClick={() => router.push("/settings")}>
              <User className="w-4 h-4 mr-2" /> Manage profile
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" onClick={toggleTheme}>
              {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" asChild>
              <a href="https://docs.specstory.com/cloud/overview" target="_blank" rel="noopener noreferrer">
                <HelpCircle className="w-4 h-4 mr-2" /> Help &amp; docs
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" asChild>
              <a href="https://docs.specstory.com/api-reference/introduction" target="_blank" rel="noopener noreferrer">
                <Code className="w-4 h-4 mr-2" /> API reference
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer text-destructive focus:text-destructive"
              onClick={() => {
                signOut();
                router.push("/");
              }}
            >
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
